/**
 * A small, safe Markdown renderer for the role briefs (docs/agents/<role>.md): headings, paragraphs, lists (nested by
 * indent), fenced code, inline code, bold / italic, links, pipe tables, blockquotes, rules. It builds React elements
 * (text is always escaped by React; raw HTML is shown as text, never parsed) and only keeps links to http(s), mailto,
 * in-app paths and anchors, so `javascript:` and friends render as plain text.
 */
import type { ReactNode } from "react";

export function safeHref(href: string): string | null {
  const h = href.trim();
  if (/^(https?:|mailto:)/i.test(h)) return h;
  if (h.startsWith("//")) return null;
  if (/^[/#?.]/.test(h)) return h;
  if (/^[a-z][a-z0-9+.-]*:/i.test(h)) return null; // any other scheme (javascript:, data:, vbscript:, …)
  return h; // relative path
}

const INLINE = new RegExp(
  [
    "(`+)([\\s\\S]*?)\\1", // 1,2 code span
    "\\[([^\\]]+)\\]\\(([^)\\s]+)(?:\\s+\"[^\"]*\")?\\)", // 3,4 link
    "<(https?://[^>\\s]+)>", // 5 autolink
    "\\*\\*([^*]+)\\*\\*", // 6 bold
    "(?<![A-Za-z0-9])__([^_]+)__(?![A-Za-z0-9])", // 7 bold
    "\\*([^*\\s](?:[^*]*[^*\\s])?)\\*", // 8 italic
    "(?<![A-Za-z0-9])_([^_\\s](?:[^_]*[^_\\s])?)_(?![A-Za-z0-9])", // 9 italic
  ].join("|"),
  "g",
);

export function renderInline(text: string, key = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const k = `${key}-${n++}`;
    if (m[2] !== undefined) out.push(<code key={k}>{m[2]}</code>);
    else if (m[3] !== undefined && m[4] !== undefined) out.push(link(m[4], renderInline(m[3], k), k, m[0]));
    else if (m[5] !== undefined) out.push(link(m[5], [m[5]], k, m[0]));
    else if (m[6] !== undefined || m[7] !== undefined) out.push(<strong key={k}>{renderInline(m[6] ?? m[7] ?? "", k)}</strong>);
    else if (m[8] !== undefined || m[9] !== undefined) out.push(<em key={k}>{renderInline(m[8] ?? m[9] ?? "", k)}</em>);
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function link(href: string, children: ReactNode[], key: string, raw: string): ReactNode {
  const h = safeHref(href);
  if (h === null) return raw;
  const external = /^https?:/i.test(h);
  return (
    <a key={key} href={h} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
      {children}
    </a>
  );
}

type ListItem = { text: string[]; children: ListBlock | null };
type ListBlock = { ordered: boolean; start: number; items: ListItem[] };

const LIST_RE = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const TABLE_SEP = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;
const FENCE = /^\s*(```|~~~)(.*)$/;

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && s[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (s[i] === "|") {
      cells.push(cur.trim());
      cur = "";
    } else cur += s[i];
  }
  cells.push(cur.trim());
  return cells;
}

function parseList(lines: string[], start: number): [ListBlock, number] {
  const first = LIST_RE.exec(lines[start] ?? "");
  const indent = first?.[1]?.length ?? 0;
  const ordered = /\d/.test(first?.[2] ?? "");
  const block: ListBlock = { ordered, start: ordered ? Number.parseInt(first?.[2] ?? "1", 10) : 1, items: [] };
  let i = start;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const m = LIST_RE.exec(line);
    if (m) {
      const ind = m[1]?.length ?? 0;
      if (ind < indent) break;
      if (ind > indent) {
        const last = block.items[block.items.length - 1];
        if (!last) break;
        const [child, next] = parseList(lines, i);
        last.children = child;
        i = next;
        continue;
      }
      if (/\d/.test(m[2] ?? "") !== ordered) break;
      block.items.push({ text: [m[3] ?? ""], children: null });
      i++;
      continue;
    }
    const last = block.items[block.items.length - 1];
    if (line.trim() && last && /^\s+/.test(line) && !FENCE.test(line)) {
      last.text.push(line.trim()); // lazy continuation line
      i++;
      continue;
    }
    break;
  }
  return [block, i];
}

function renderList(b: ListBlock, key: string): ReactNode {
  const items = b.items.map((it, j) => (
    <li key={j}>
      {renderInline(it.text.join(" "), `${key}-${j}`)}
      {it.children && renderList(it.children, `${key}-${j}c`)}
    </li>
  ));
  return b.ordered ? <ol key={key} start={b.start === 1 ? undefined : b.start}>{items}</ol> : <ul key={key}>{items}</ul>;
}

export function renderMarkdown(src: string): ReactNode[] {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const out: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const key = `b${i}`;
    if (!line.trim()) {
      i++;
      continue;
    }
    const fence = FENCE.exec(line);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").trim().startsWith(fence[1] ?? "```")) body.push(lines[i++] ?? "");
      i++;
      const lang = (fence[2] ?? "").trim();
      out.push(<pre key={key} className="md-code"><code data-lang={lang || undefined}>{body.join("\n")}</code></pre>);
      continue;
    }
    const h = /^(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
    if (h) {
      const level = h[1]?.length ?? 1;
      const content = renderInline(h[2] ?? "", key);
      const Tag = `h${Math.min(6, level + 1)}` as "h2"; // the page owns h1
      out.push(<Tag key={key}>{content}</Tag>);
      i++;
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      out.push(<hr key={key} />);
      i++;
      continue;
    }
    if (line.includes("|") && TABLE_SEP.test(lines[i + 1] ?? "") && (lines[i + 1] ?? "").includes("-")) {
      const head = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && (lines[i] ?? "").includes("|") && (lines[i] ?? "").trim()) rows.push(splitRow(lines[i++] ?? ""));
      out.push(
        <div key={key} className="md-table" role="region" aria-label="Table" tabIndex={0}>
          <table className="ws7-table">
            <thead><tr>{head.map((c, j) => <th key={j} scope="col">{renderInline(c, `${key}h${j}`)}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>{head.map((_, j) => <td key={j}>{renderInline(r[j] ?? "", `${key}r${ri}c${j}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^\s*>/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i] ?? "")) body.push((lines[i++] ?? "").replace(/^\s*>\s?/, ""));
      out.push(<blockquote key={key}>{renderMarkdown(body.join("\n"))}</blockquote>);
      continue;
    }
    if (LIST_RE.test(line)) {
      const [block, next] = parseList(lines, i);
      out.push(renderList(block, key));
      i = next;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? "";
      if (!l.trim() || FENCE.test(l) || /^#{1,6}\s/.test(l) || /^\s*>/.test(l) || LIST_RE.test(l)
        || (l.includes("|") && TABLE_SEP.test(lines[i + 1] ?? ""))) break;
      para.push(l.trim());
      i++;
    }
    if (para.length === 0) {
      para.push(line.trim()); // never stall on a line no block rule took
      i++;
    }
    out.push(<p key={key}>{renderInline(para.join(" "), key)}</p>);
  }
  return out;
}

export function Markdown({ source, testId }: { source: string; testId?: string }) {
  return <div className="md-body" data-testid={testId}>{renderMarkdown(source)}</div>;
}
